import { type DefaultOptions, MutationCache, QueryCache, QueryClient, type QueryMeta } from '@tanstack/react-query'
import { TRPCClientError } from '@trpc/client'
import { type UiErrorDetail } from '../components/ErrorModal/ErrorModal'
import { toTrpcUiError } from '../utils/TrpcErrorMessages'
import type { ConnectivityStatus } from './ConnectivityProvider'

type TelemetryFn = (error: unknown, context: { kind: 'query' | 'mutation'; meta?: BreezyQueryMeta }) => void
type OnNetworkError = (ctx: { error: unknown; kind: 'query' | 'mutation'; meta?: BreezyQueryMeta }) => void

export type BreezyQueryErrorPolicy = 'modal' | 'silent' | 'toast'

export type BreezyQueryMeta = QueryMeta & {
  errorPolicy?: BreezyQueryErrorPolicy
  dedupeKey?: string
}

export type BreezySurface = 'office' | 'tech'

type CreateQueryClientOptions = {
  surface: BreezySurface
  pushError: (error: UiErrorDetail) => void
  pushToast?: (error: UiErrorDetail) => void
  telemetry?: TelemetryFn
  defaultOptionsOverrides?: DefaultOptions
  onNetworkError?: OnNetworkError
  getConnectivityStatus?: () => ConnectivityStatus
  defaultErrorPolicy?: BreezyQueryErrorPolicy
}

const DEFAULT_MAX_QUERY_RETRIES = 2
const DEFAULT_MAX_MUTATION_RETRIES = 1

const surfaceDefaults: Record<BreezySurface, { staleTime: number; cacheTime: number }> = {
  office: {
    staleTime: 60_000,
    cacheTime: 600_000,
  },
  tech: {
    staleTime: 120_000,
    cacheTime: 300_000,
  },
}

const isRetriableStatus = (status?: number): boolean => {
  if (status === undefined) return true
  if (status === 408 || status === 429) return true
  return status >= 500 && status < 600
}

const getHttpStatus = (error: unknown): number | undefined => {
  if (error instanceof TRPCClientError) return error.data?.httpStatus
  if (error && typeof error === 'object' && 'status' in error) {
    const maybeStatus = (error as { status?: unknown }).status
    if (typeof maybeStatus === 'number') return maybeStatus
  }
  return undefined
}

const isAbortError = (cause: unknown): boolean => {
  if (!cause || typeof cause !== 'object') return false
  return (cause as { name?: string }).name === 'AbortError'
}

const isNetworkClassError = (error: unknown): boolean => {
  if (error instanceof TRPCClientError && isAbortError(error.cause)) return false
  return getHttpStatus(error) === undefined
}

const shouldRetry = (
  failureCount: number,
  error: unknown,
  maxRetries: number,
  getConnectivityStatus?: () => ConnectivityStatus,
): boolean => {
  if (failureCount >= maxRetries) return false
  if (error instanceof TRPCClientError && isAbortError(error.cause)) return false

  const httpStatus = getHttpStatus(error)
  const status = getConnectivityStatus?.()
  const unhealthy = status === 'degraded' || status === 'offline'

  if (httpStatus === undefined && (unhealthy || navigator.onLine === false)) {
    return false
  }

  return isRetriableStatus(httpStatus)
}

const retryDelay = (attemptIndex: number): number => {
  const base = 500
  const jitter = Math.random() * 1000
  const incremental = attemptIndex * 250
  return Math.min(base + jitter + incremental, 5_000)
}

const isBreezyQueryMeta = (meta: unknown): meta is BreezyQueryMeta => {
  if (!meta || typeof meta !== 'object') return false
  const candidate = meta as Record<string, unknown>
  const { errorPolicy, dedupeKey } = candidate
  const policyIsValid =
    errorPolicy === undefined || errorPolicy === 'modal' || errorPolicy === 'silent' || errorPolicy === 'toast'
  const dedupeIsValid = dedupeKey === undefined || typeof dedupeKey === 'string'
  return policyIsValid && dedupeIsValid
}

const buildUiError = (
  error: unknown,
  defaultVerb: 'query' | 'execute',
  meta?: BreezyQueryMeta,
): UiErrorDetail | undefined => {
  if (error instanceof TRPCClientError) {
    const uiError = toTrpcUiError(error, defaultVerb)
    if (!uiError) return undefined
    return { ...uiError, dedupeKey: uiError.dedupeKey ?? meta?.dedupeKey }
  }

  return {
    title: 'Request failed',
    content: 'Unable to complete the request. Please try again.',
    dedupeKey: meta?.dedupeKey,
  }
}

const buildDefaultOptions = (
  surface: BreezySurface,
  overrides?: DefaultOptions,
  getConnectivityStatus?: () => ConnectivityStatus,
): DefaultOptions => {
  const { cacheTime, staleTime } = surfaceDefaults[surface]
  const base: DefaultOptions = {
    queries: {
      networkMode: 'always',
      staleTime,
      cacheTime,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: true,
      retry: (failureCount, error) =>
        shouldRetry(failureCount, error, DEFAULT_MAX_QUERY_RETRIES, getConnectivityStatus),
      retryDelay,
    },
    mutations: {
      networkMode: 'always',
      retry: (failureCount, error) =>
        shouldRetry(failureCount, error, DEFAULT_MAX_MUTATION_RETRIES, getConnectivityStatus),
      retryDelay,
    },
  }

  return {
    queries: { ...base.queries, ...overrides?.queries },
    mutations: { ...base.mutations, ...overrides?.mutations },
  }
}

const routeError =
  (
    pushError: (error: UiErrorDetail) => void,
    pushToast: ((error: UiErrorDetail) => void) | undefined,
    telemetry: TelemetryFn | undefined,
    defaultVerb: 'query' | 'execute',
    onNetworkError?: OnNetworkError,
    defaultErrorPolicy: BreezyQueryErrorPolicy = 'modal',
  ) =>
  (error: unknown, meta: BreezyQueryMeta | undefined, kind: 'query' | 'mutation'): void => {
    telemetry?.(error, { kind, meta })

    const policy = meta?.errorPolicy ?? defaultErrorPolicy
    if (policy === 'silent') return

    if (isNetworkClassError(error)) {
      onNetworkError?.({ error, kind, meta })
      return
    }

    const uiError = buildUiError(error, defaultVerb, meta)
    if (!uiError) return

    if (policy === 'toast') {
      if (pushToast) {
        pushToast(uiError)
      } else {
        pushError(uiError)
      }
      return
    }

    pushError(uiError)
  }

export const createQueryClient = ({
  surface,
  pushError,
  pushToast,
  telemetry,
  defaultOptionsOverrides,
  onNetworkError,
  getConnectivityStatus,
  defaultErrorPolicy,
}: CreateQueryClientOptions) => {
  const defaultOptions = buildDefaultOptions(surface, defaultOptionsOverrides, getConnectivityStatus)
  const handleError = routeError(pushError, pushToast, telemetry, 'query', onNetworkError, defaultErrorPolicy)
  const handleMutationError = routeError(pushError, pushToast, telemetry, 'execute', onNetworkError, defaultErrorPolicy)

  const queryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        const meta = isBreezyQueryMeta(query.meta) ? query.meta : undefined
        handleError(error, meta, 'query')
      },
    }),
    mutationCache: new MutationCache({
      onSuccess: (_data, _variables, _context, mutation) => {
        const path = mutation.options.mutationKey?.[0]
        if (
          Array.isArray(path) &&
          [
            'jobs',
            'appointments',
            'assignments',
            'scheduling',
            'accounts',
            'accountMerge',
            'location',
            'estimates',
            'invoice',
            'payments',
          ].includes(path[0])
        ) {
          void queryClient.invalidateQueries({
            predicate: query =>
              query.queryKey[0] === 'service-history-availability' || query.queryKey[0] === 'service-history',
          })
        }
      },
      onError: (error, _variables, _context, mutation) => {
        const meta = isBreezyQueryMeta(mutation.meta) ? mutation.meta : undefined
        handleMutationError(error, meta, 'mutation')
      },
    }),
    defaultOptions,
  })
  return queryClient
}

export const breezyQueryDefaults = surfaceDefaults
export const breezyRetryDelay = retryDelay
export const breezyShouldRetry = shouldRetry
